import { Box, Button, Text, useToast, Progress, VStack, HStack, Badge } from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { AttachmentIcon } from '@chakra-ui/icons';

interface LatestDates {
  westchester?: string;
  putnam?: string;
  rockland?: string;
  misc?: string;
}

export function FileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingWestchester, setIsUploadingWestchester] = useState(false);
  const [isUploadingPutnam, setIsUploadingPutnam] = useState(false);
  const [isUploadingRockland, setIsUploadingRockland] = useState(false);
  const [processedFiles, setProcessedFiles] = useState<Set<string>>(new Set());
  const [lastUploadedFile, setLastUploadedFile] = useState<string>('');
  const [lastUploadTime, setLastUploadTime] = useState<string>('');
  const [latestDates, setLatestDates] = useState<LatestDates>({});
  const toast = useToast();

  // Fetch processed files and latest booking dates on mount
  useEffect(() => {
    fetchProcessedFiles();
    fetchLatestDates();
  }, []);

  const fetchLatestDates = async () => {
    try {
      const response = await fetch('https://slack.westchesterrtc.com/insight/api/admin/latest-booking-dates', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLatestDates(data);
      }
    } catch (error) {
      console.error('Error fetching latest dates:', error);
    }
  };

  const fetchProcessedFiles = async () => {
    try {
      const response = await fetch('https://slack.westchesterrtc.com/insight/api/admin/processed-files', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProcessedFiles(new Set(data.processed_files));
      }
    } catch (error) {
      console.error('Error fetching processed files:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>, uploadType: string) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Filter out already processed files
    const alreadyProcessedFiles = Array.from(files).filter(file => processedFiles.has(file.name));
    const newFiles = Array.from(files).filter(file => !processedFiles.has(file.name));

    if (newFiles.length === 0) {
      toast({
        title: 'Files Already Processed',
        description: `The following files have already been processed: ${alreadyProcessedFiles.map(f => f.name).join(', ')}`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
      event.target.value = '';
      return;
    }

    if (alreadyProcessedFiles.length > 0) {
      toast({
        title: 'Some Files Already Processed',
        description: `Skipping already processed files: ${alreadyProcessedFiles.map(f => f.name).join(', ')}`,
        status: 'info',
        duration: 5000,
        isClosable: true,
      });
    }

    // Set the appropriate uploading state
    let setUploadingState;
    switch (uploadType) {
      case 'misc':
        setUploadingState = setIsUploading;
        break;
      case 'westchester':
        setUploadingState = setIsUploadingWestchester;
        break;
      case 'putnam':
        setUploadingState = setIsUploadingPutnam;
        break;
      case 'rockland':
        setUploadingState = setIsUploadingRockland;
        break;
      default:
        return;
    }
    setUploadingState(true);

    try {
      const formData = new FormData();
      newFiles.forEach(file => {
        formData.append('files', file);
      });

      // Determine the endpoint based on upload type
      let endpoint;
      switch (uploadType) {
        case 'misc':
          endpoint = 'https://slack.westchesterrtc.com/insight/api/admin/upload-documents';
          break;
        case 'westchester':
          endpoint = 'https://slack.westchesterrtc.com/insight/api/admin/upload-westchester-rici';
          break;
        case 'putnam':
          endpoint = 'https://slack.westchesterrtc.com/insight/api/admin/upload-putnam-rici';
          break;
        case 'rockland':
          endpoint = 'https://slack.westchesterrtc.com/insight/api/admin/upload-rockland-rici';
          break;
        default:
          return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      
      await fetchProcessedFiles();
      if (uploadType === 'misc') {
        setLastUploadedFile(newFiles[newFiles.length - 1].name);
        setLastUploadTime(new Date().toLocaleString());
      } else {
        await fetchLatestDates();
      }

      toast({
        title: 'Upload Successful',
        description: `${data.files.length} new files uploaded successfully`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: error instanceof Error ? error.message : 'An error occurred during upload',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploadingState(false);
      event.target.value = '';
    }
  };

  return (
    <Box p={4} borderWidth={1} borderRadius="lg" bg="gray.800">
      <Text mb={4} fontSize="lg" fontWeight="bold">
        Upload Documents
      </Text>
      
      <VStack spacing={4}>
        {/* Misc Intel Upload */}
        <Box width="100%">
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="gray.400">
              {latestDates.misc && (
                <>
                  Last processed: <Badge colorScheme="blue">{new Date(latestDates.misc).toLocaleString()}</Badge>
                </>
              )}
            </Text>
          </HStack>
          <input
            type="file"
            multiple
            onChange={(e) => handleFileUpload(e, 'misc')}
            style={{ display: 'none' }}
            id="misc-file-upload"
          />
          
          <label htmlFor="misc-file-upload">
            <Button
              as="span"
              leftIcon={<AttachmentIcon />}
              colorScheme="blue"
              isLoading={isUploading}
              cursor="pointer"
              width="full"
            >
              Upload Misc Intel Files
            </Button>
          </label>

          {isUploading && (
            <Progress 
              size="sm" 
              isIndeterminate 
              colorScheme="blue" 
              mt={4} 
            />
          )}
        </Box>

        {/* Westchester RICI Upload */}
        <Box width="100%">
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="gray.400">
              Latest booking: <Badge colorScheme="blue">{latestDates.westchester || 'No data'}</Badge>
            </Text>
          </HStack>
          <input
            type="file"
            multiple
            onChange={(e) => handleFileUpload(e, 'westchester')}
            style={{ display: 'none' }}
            id="westchester-file-upload"
          />
          
          <label htmlFor="westchester-file-upload">
            <Button
              as="span"
              leftIcon={<AttachmentIcon />}
              colorScheme="blue"
              isLoading={isUploadingWestchester}
              cursor="pointer"
              width="full"
            >
              Upload Westchester RICI Files
            </Button>
          </label>

          {isUploadingWestchester && (
            <Progress 
              size="sm" 
              isIndeterminate 
              colorScheme="blue" 
              mt={4} 
            />
          )}
        </Box>

        {/* Putnam RICI Upload */}
        <Box width="100%">
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="gray.400">
              Latest booking: <Badge colorScheme="green">{latestDates.putnam || 'No data'}</Badge>
            </Text>
          </HStack>
          <input
            type="file"
            multiple
            onChange={(e) => handleFileUpload(e, 'putnam')}
            style={{ display: 'none' }}
            id="putnam-file-upload"
          />
          
          <label htmlFor="putnam-file-upload">
            <Button
              as="span"
              leftIcon={<AttachmentIcon />}
              colorScheme="green"
              isLoading={isUploadingPutnam}
              cursor="pointer"
              width="full"
            >
              Upload Putnam RICI Files
            </Button>
          </label>

          {isUploadingPutnam && (
            <Progress 
              size="sm" 
              isIndeterminate 
              colorScheme="green" 
              mt={4} 
            />
          )}
        </Box>

        {/* Rockland RICI Upload */}
        <Box width="100%">
          <HStack justify="space-between" mb={2}>
            <Text fontSize="sm" color="gray.400">
              Latest booking: <Badge colorScheme="cyan">{latestDates.rockland || 'No data'}</Badge>
            </Text>
          </HStack>
          <input
            type="file"
            multiple
            onChange={(e) => handleFileUpload(e, 'rockland')}
            style={{ display: 'none' }}
            id="rockland-file-upload"
          />
          
          <label htmlFor="rockland-file-upload">
            <Button
              as="span"
              leftIcon={<AttachmentIcon />}
              colorScheme="cyan"
              isLoading={isUploadingRockland}
              cursor="pointer"
              width="full"
            >
              Upload Rockland RICI Files
            </Button>
          </label>

          {isUploadingRockland && (
            <Progress 
              size="sm" 
              isIndeterminate 
              colorScheme="cyan" 
              mt={4} 
            />
          )}
        </Box>
      </VStack>
    </Box>
  );
} 